/**
 * Vendored React 19-compatible replacement for the `usePopper` hook from
 * the unmaintained `react-popper` package.
 *
 * Changes from the original:
 * - Ported to TypeScript (the original was Flow-typed)
 * - Replaced `ReactDOM.flushSync` with a plain `setState` call. In React 18+
 *   `flushSync` inside a `useLayoutEffect` callback causes a warning; in
 *   React 19 it throws. The first position is instead computed synchronously
 *   via `forceUpdate()` while still inside the layout effect, which React
 *   commits before the browser paints (see the effect below).
 * - Inlined `fromEntries` and `isEqual` helpers to remove the
 *   `react-fast-compare` dependency.
 * - `useIsomorphicLayoutEffect` inlined: uses `useLayoutEffect` in browsers
 *   and `useEffect` as an SSR fallback to suppress the React warning.
 *
 * Source: https://github.com/floating-ui/react-popper/blob/master/src/usePopper.js
 */
import {
  createPopper as defaultCreatePopper,
  type Instance as PopperInstance,
  type Options as PopperOptions,
  type State as PopperState,
  type VirtualElement,
} from '@popperjs/core';
import {
  type CSSProperties,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

const useIsomorphicLayoutEffect =
  typeof document !== 'undefined' ? useLayoutEffect : useEffect;

function fromEntries<T>(entries: Array<[string, T]>): Record<string, T> {
  return entries.reduce<Record<string, T>>((acc, [key, value]) => {
    acc[key] = value;
    return acc;
  }, {});
}

function deepEqual(a: unknown, b: unknown): boolean {
  if (Object.is(a, b)) {
    return true;
  }
  if (typeof a !== 'object' || typeof b !== 'object') {
    return false;
  }
  if (a === null || b === null) {
    return false;
  }
  const keysA = Object.keys(a as object);
  const keysB = Object.keys(b as object);
  if (keysA.length !== keysB.length) {
    return false;
  }
  return keysA.every((key) =>
    deepEqual(
      (a as Record<string, unknown>)[key],
      (b as Record<string, unknown>)[key],
    ),
  );
}

type Styles = Record<string, CSSProperties>;
type Attributes = Record<string, Record<string, string | boolean>>;

type UsePopperState = {
  styles: Styles;
  attributes: Attributes;
};

type UsePopperOptions = Partial<
  PopperOptions & { createPopper: typeof defaultCreatePopper }
>;

export type UsePopperResult = Readonly<{
  state: PopperState | null;
  styles: Styles;
  attributes: Attributes;
  update: PopperInstance['update'] | null;
  forceUpdate: PopperInstance['forceUpdate'] | null;
}>;

const EMPTY_MODIFIERS: PopperOptions['modifiers'] = [];

export function usePopper(
  referenceElement: Element | VirtualElement | null | undefined,
  popperElement: HTMLElement | null | undefined,
  options: UsePopperOptions = {},
): UsePopperResult {
  const prevOptions = useRef<PopperOptions | null>(null);

  const optionsWithDefaults = {
    onFirstUpdate: options.onFirstUpdate,
    placement: options.placement ?? 'bottom',
    strategy: options.strategy ?? 'absolute',
    modifiers: options.modifiers ?? EMPTY_MODIFIERS,
  };

  const [state, setState] = useState<UsePopperState>({
    styles: {
      popper: {
        position: optionsWithDefaults.strategy,
        left: '0',
        top: '0',
      },
      arrow: {
        position: 'absolute',
      },
    },
    attributes: {},
  });

  const updateStateModifier = useMemo(
    () => ({
      name: 'updateState',
      enabled: true,
      phase: 'write' as const,
      fn: ({ state: popperState }: { state: PopperState }) => {
        const elements = Object.keys(popperState.elements) as Array<
          keyof typeof popperState.elements
        >;
        // React 19 fix: removed ReactDOM.flushSync() wrapper. In React 18+
        // calling flushSync inside useLayoutEffect throws.
        const nextState: UsePopperState = {
          styles: fromEntries(
            elements.map((el) => [
              el as string,
              (popperState.styles[el as string] as CSSProperties) ?? {},
            ]),
          ),
          attributes: fromEntries(
            elements.map((el) => [
              el as string,
              popperState.attributes[el as string] ?? {},
            ]),
          ),
        };

        // The eager forceUpdate() below and the update that `createPopper`
        // queues itself both land here with identical results. Bailing out on
        // an unchanged state keeps that from causing a second render.
        setState((currentState) =>
          deepEqual(currentState, nextState) ? currentState : nextState,
        );
      },
      requires: ['computeStyles'],
    }),
    [],
  );

  const popperOptions = useMemo(() => {
    const newOptions: PopperOptions = {
      onFirstUpdate: optionsWithDefaults.onFirstUpdate,
      placement: optionsWithDefaults.placement,
      strategy: optionsWithDefaults.strategy,
      modifiers: [
        ...optionsWithDefaults.modifiers,
        updateStateModifier,
        { name: 'applyStyles', enabled: false },
      ],
    };

    if (deepEqual(prevOptions.current, newOptions)) {
      return prevOptions.current ?? newOptions;
    } else {
      prevOptions.current = newOptions;
      return newOptions;
    }
  }, [
    optionsWithDefaults.onFirstUpdate,
    optionsWithDefaults.placement,
    optionsWithDefaults.strategy,
    optionsWithDefaults.modifiers,
    updateStateModifier,
  ]);

  const popperInstanceRef = useRef<PopperInstance | undefined>(undefined);

  useIsomorphicLayoutEffect(() => {
    if (popperInstanceRef.current) {
      void popperInstanceRef.current.setOptions(popperOptions);
    }
  }, [popperOptions]);

  useIsomorphicLayoutEffect(() => {
    if (
      referenceElement === null ||
      referenceElement === undefined ||
      popperElement === null ||
      popperElement === undefined
    ) {
      return;
    }

    const createPopper = options.createPopper ?? defaultCreatePopper;
    let popperInstance: PopperInstance | undefined;
    if (prevOptions.current) {
      popperInstance = createPopper(
        referenceElement,
        popperElement,
        prevOptions.current,
      );
      popperInstanceRef.current = popperInstance;

      // `createPopper` only schedules its first update, and popper debounces
      // that onto a microtask. Because the state update is no longer flushed
      // with flushSync, React can commit it after the browser has already
      // painted the initial (0,0) position, which shows up as the overlay
      // flashing in the top-left corner (APPDEV-19359). forceUpdate() is
      // synchronous, so calling it here makes the positioned styles part of the
      // render that React flushes before paint.
      popperInstance.forceUpdate();
    }

    return () => {
      popperInstance?.destroy();
      popperInstanceRef.current = undefined;
    };
  }, [referenceElement, popperElement, options.createPopper]);

  return {
    state: popperInstanceRef.current ? popperInstanceRef.current.state : null,
    styles: state.styles,
    attributes: state.attributes,
    update: popperInstanceRef.current ? popperInstanceRef.current.update : null,
    forceUpdate: popperInstanceRef.current
      ? popperInstanceRef.current.forceUpdate
      : null,
  };
}
